import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { Bug, Lightbulb, Heart, Question as HelpCircle, ArrowsClockwise as RefreshCw, MagnifyingGlass as Search, CaretRight as ChevronRight, ArrowSquareOut as ExternalLink, Image as ImageIcon, X, Check, CircleNotch as Loader2, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LoadingState, ErrorState } from "@/components/states";
import {
  fetchFeedback,
  updateFeedbackStatus,
  deleteFeedback,
  getFeedbackImageSignedUrl,
  type FeedbackItem,
  type FeedbackType,
  type FeedbackStatus,
  type FeedbackFilter,
} from "../api/feedbackApi";
import { toast } from "sonner";
import { toastError } from "@/lib/userMessages";

// ── Badges ────────────────────────────────────────────────────────────────

function typeBadge(type: FeedbackType) {
  const map: Record<FeedbackType, { label: string; cls: string; icon: React.ReactNode }> = {
    bug: { label: "Bug", cls: "bg-destructive/15 text-red-400 border-destructive/30", icon: <Bug className="w-3 h-3" /> },
    suggestion: { label: "Sugestão", cls: "bg-warning/15 text-yellow-400 border-warning/30", icon: <Lightbulb className="w-3 h-3" /> },
    praise: { label: "Elogio", cls: "bg-pink-500/15 text-pink-400 border-pink-500/30", icon: <Heart className="w-3 h-3" /> },
    other: { label: "Outro", cls: "bg-muted text-muted-foreground", icon: <HelpCircle className="w-3 h-3" /> },
  };
  const { label, cls, icon } = map[type] ?? map.other;
  return (
    <Badge className={`gap-1 ${cls}`}>
      {icon} {label}
    </Badge>
  );
}

function statusBadge(status: FeedbackStatus) {
  const map: Record<FeedbackStatus, { label: string; cls: string }> = {
    open: { label: "Aberto", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
    in_progress: { label: "Em análise", cls: "bg-warning/15 text-yellow-400 border-warning/30" },
    resolved: { label: "Resolvido", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
    closed: { label: "Fechado", cls: "bg-muted text-muted-foreground" },
  };
  const { label, cls } = map[status] ?? map.open;
  return <Badge className={cls}>{label}</Badge>;
}

// ── Detail drawer ─────────────────────────────────────────────────────────

function FeedbackDetail({
  item,
  onClose,
}: {
  item: FeedbackItem;
  onClose: () => void;
}) {
  const { client } = useSupabaseClient();
  const qc = useQueryClient();
  const [status, setStatus] = useState<FeedbackStatus>(item.status);
  const [notes, setNotes] = useState(item.admin_notes ?? "");
  const [lightbox, setLightbox] = useState<string | null>(null);

  const { mutate: save, isPending } = useMutation({
    mutationFn: () => updateFeedbackStatus(client, item.id, status, notes),
    onSuccess: () => {
      toast.success("Feedback atualizado.");
      qc.invalidateQueries({ queryKey: ["admin", "feedback"] });
    },
    onError: (e) => toastError(e, "Não foi possível salvar. Tente novamente."),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
          <div className="flex items-center gap-2 min-w-0">
            {typeBadge(item.type)}
            <span className="text-sm font-semibold text-foreground truncate">{item.title}</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground ml-3 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-5 py-4 space-y-5">
            {/* Meta */}
            <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
              <div>
                <span className="font-medium text-foreground/70">Usuário</span>
                {item.user_name && <p className="mt-0.5 font-medium text-foreground/90">{item.user_name}</p>}
                <p className="mt-0.5">{item.user_email ?? "Anônimo"}</p>
              </div>
              <div>
                <span className="font-medium text-foreground/70">Data</span>
                <p className="mt-0.5">{new Date(item.created_at).toLocaleString("pt-BR")}</p>
              </div>
              {item.page_url && (
                <div className="col-span-2">
                  <span className="font-medium text-foreground/70">Página</span>
                  <a
                    href={item.page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0.5 flex items-center gap-1 text-primary hover:underline truncate"
                  >
                    {item.page_url}
                    <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                Descrição
              </p>
              <p className="text-sm text-foreground/90 whitespace-pre-wrap">{item.description}</p>
            </div>

            {/* Steps */}
            {item.steps && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  Como reproduzir
                </p>
                <pre className="text-xs text-foreground/80 bg-muted/40 rounded-lg p-3 whitespace-pre-wrap font-mono">
                  {item.steps}
                </pre>
              </div>
            )}

            {/* Images */}
            {item.image_paths.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Imagens ({item.image_paths.length})
                </p>
                <div className="flex gap-2 flex-wrap">
                  {item.image_paths.map((path, i) => (
                    <FeedbackImageThumb
                      key={i}
                      client={client}
                      path={path}
                      onClick={(url) => setLightbox(url)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* User agent (collapsed) */}
            {item.user_agent && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground/60 hover:text-muted-foreground">
                  Detalhes técnicos (user agent)
                </summary>
                <p className="mt-1 font-mono text-[10px] text-muted-foreground/50 break-all">
                  {item.user_agent}
                </p>
              </details>
            )}

            {/* Admin controls */}
            <div className="border-t border-border/60 pt-4 space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Gerenciar
              </p>
              <div className="flex gap-2">
                <Select value={status} onValueChange={(v) => setStatus(v as FeedbackStatus)}>
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Aberto</SelectItem>
                    <SelectItem value="in_progress">Em análise</SelectItem>
                    <SelectItem value="resolved">Resolvido</SelectItem>
                    <SelectItem value="closed">Fechado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas internas (não visíveis ao usuário)…"
                className="text-xs min-h-[60px] resize-none"
              />
              <Button size="sm" onClick={() => save()} disabled={isPending}>
                {isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                Salvar
              </Button>
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-lg shadow-2xl" />
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white"
            onClick={() => setLightbox(null)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────

function FeedbackRow({
  item,
  onSelect,
  checked,
  onToggle,
  onDelete,
}: {
  item: FeedbackItem;
  onSelect: (item: FeedbackItem) => void;
  checked: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <tr
      className="border-b border-border/40 hover:bg-muted/30 cursor-pointer transition-colors"
      onClick={() => onSelect(item)}
    >
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={checked} onCheckedChange={onToggle} aria-label="Selecionar feedback" />
      </td>
      <td className="px-3 py-2.5">{typeBadge(item.type)}</td>
      <td className="px-3 py-2.5 max-w-[240px]">
        <p className="text-sm text-foreground truncate">{item.title}</p>
        <p className="text-xs text-muted-foreground truncate">{item.description}</p>
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
        <div className="flex flex-col gap-0.5">
          {item.user_name && <span className="text-foreground/80 font-medium">{item.user_name}</span>}
          <span>{item.user_email ?? "—"}</span>
        </div>
      </td>
      <td className="px-3 py-2.5">{statusBadge(item.status)}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
        {item.image_paths.length > 0 && (
          <span className="flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            {item.image_paths.length}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
        {new Date(item.created_at).toLocaleDateString("pt-BR")}
      </td>
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          onClick={onDelete}
          aria-label="Excluir feedback"
        >
          <Trash className="w-3.5 h-3.5" />
        </Button>
      </td>
      <td className="px-3 py-2.5 text-muted-foreground">
        <ChevronRight className="w-4 h-4" />
      </td>
    </tr>
  );
}

// ── Main section ──────────────────────────────────────────────────────────

export function FeedbackSection() {
  const { client, isReady } = useSupabaseClient();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<FeedbackType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | "all">("all");
  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<{ ids: string[]; label: string } | null>(null);

  const filter: FeedbackFilter = {
    type: typeFilter !== "all" ? typeFilter : undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    search: search.trim() || undefined,
  };

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "feedback", filter],
    queryFn: () => fetchFeedback(client, filter),
    enabled: isReady,
    staleTime: 30_000,
  });

  const items = data ?? [];

  const { mutate: runDelete, isPending: isDeleting } = useMutation({
    mutationFn: (ids: string[]) => deleteFeedback(client, ids),
    onSuccess: (_, ids) => {
      toast.success(ids.length === 1 ? "Feedback excluído." : `${ids.length} feedbacks excluídos.`);
      setSelectedIds(new Set());
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ["admin", "feedback"] });
    },
    onError: (e) => toastError(e, "Não foi possível excluir. Tente novamente."),
  });

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  // Stats
  const openCount = items.filter((i) => i.status === "open").length;
  const bugCount = items.filter((i) => i.type === "bug").length;

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="flex gap-4 flex-wrap">
        {[
          { label: "Total", value: items.length, cls: "text-foreground" },
          { label: "Abertos", value: openCount, cls: "text-blue-400" },
          { label: "Bugs", value: bugCount, cls: "text-red-400" },
        ].map(({ label, value, cls }) => (
          <div key={label} className="bg-muted/40 rounded-lg px-4 py-2 text-center min-w-[80px]">
            <p className={`text-xl font-bold ${cls}`}>{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título, descrição ou email…"
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as FeedbackType | "all")}>
          <SelectTrigger className="h-8 text-xs w-36">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="bug">Bug</SelectItem>
            <SelectItem value="suggestion">Sugestão</SelectItem>
            <SelectItem value="praise">Elogio</SelectItem>
            <SelectItem value="other">Outro</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as FeedbackStatus | "all")}>
          <SelectTrigger className="h-8 text-xs w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="open">Aberto</SelectItem>
            <SelectItem value="in_progress">Em análise</SelectItem>
            <SelectItem value="resolved">Resolvido</SelectItem>
            <SelectItem value="closed">Fechado</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching} className="h-8 px-2">
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
        </Button>
        {selectedIds.size > 0 && (
          <Button
            variant="destructive"
            size="sm"
            className="h-8 gap-1.5"
            onClick={() =>
              setDeleteTarget({
                ids: Array.from(selectedIds),
                label: `${selectedIds.size} ${selectedIds.size === 1 ? "feedback" : "feedbacks"}`,
              })
            }
            disabled={isDeleting}
          >
            <Trash className="w-3.5 h-3.5" />
            Excluir {selectedIds.size}
          </Button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <LoadingState message="Carregando feedbacks…" />
      ) : error ? (
        <ErrorState message={error instanceof Error ? error.message : "Erro ao carregar."} />
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Nenhum feedback encontrado.
        </div>
      ) : (
        <ScrollArea className="rounded-lg border border-border/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/20">
                <th className="px-3 py-2 w-8">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={toggleAll}
                    aria-label="Selecionar todos"
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Tipo</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Título</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Usuário</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">📎</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Data</th>
                <th className="px-3 py-2 w-10" />
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <FeedbackRow
                  key={item.id}
                  item={item}
                  onSelect={setSelected}
                  checked={selectedIds.has(item.id)}
                  onToggle={() => toggleOne(item.id)}
                  onDelete={() =>
                    setDeleteTarget({ ids: [item.id], label: `"${item.title}"` })
                  }
                />
              ))}
            </tbody>
          </table>
        </ScrollArea>
      )}

      {selected && (
        <FeedbackDetail item={selected} onClose={() => setSelected(null)} />
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir feedback?</AlertDialogTitle>
            <AlertDialogDescription>
              Você vai excluir {deleteTarget?.label}. Esta ação é permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) runDelete(deleteTarget.ids);
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  Excluindo…
                </>
              ) : (
                "Excluir"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── FeedbackImageThumb ────────────────────────────────────────────────────
// Sec5: feedback-images bucket is private; resolve a 15-min signed URL
// per render and refresh on remount.

import { useEffect, useState as useStateThumb } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/types/supabase";

interface FeedbackImageThumbProps {
  client: SupabaseClient<Database>;
  path: string;
  onClick: (url: string) => void;
}

function FeedbackImageThumb({ client, path, onClick }: FeedbackImageThumbProps) {
  const [url, setUrl] = useStateThumb<string | null>(null);
  const [error, setError] = useStateThumb(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    setUrl(null);
    void getFeedbackImageSignedUrl(client, path)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [client, path]);

  if (error) {
    return (
      <div className="w-20 h-20 rounded-lg border border-destructive/40 bg-destructive/10 flex items-center justify-center text-[10px] text-destructive">
        erro
      </div>
    );
  }

  if (!url) {
    return <div className="w-20 h-20 rounded-lg border border-border bg-muted/30 animate-pulse" />;
  }

  return (
    <button
      onClick={() => onClick(url)}
      className="w-20 h-20 rounded-lg border border-border overflow-hidden hover:opacity-80 transition-opacity"
    >
      <img src={url} alt="" className="w-full h-full object-cover" />
    </button>
  );
}
